import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../components/ui/button';
import ReviewModalPortal from './reviews/review-modal-portal';

interface ProfessionalStatsProps {
  reviews: any[];
  professionalId?: string;
}

interface RatingDistribution {
  [key: number]: number;
}

interface ReviewStats {
  count: number;
  averageRating: number;
  ratingDistribution: RatingDistribution;
}

const ProfessionalStats: React.FC<ProfessionalStatsProps> = ({ reviews, professionalId }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const calculateReviewStats = (): ReviewStats => {
    const stats: ReviewStats = {
      count: reviews.length,
      averageRating: 0,
      ratingDistribution: {
        5: 0,
        4: 0,
        3: 0,
        2: 0,
        1: 0
      }
    };

    if (reviews.length === 0) return stats;

    let totalRating = 0;
    reviews.forEach(review => {
      const rating = Math.round(review.rating);
      stats.ratingDistribution[rating] = (stats.ratingDistribution[rating] || 0) + 1;
      totalRating += rating;
    });

    stats.averageRating = totalRating / reviews.length;
    return stats;
  };

  const stats = calculateReviewStats();

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6">
      {stats.count > 0 ? (
        <>
          
          <div className="flex items-center justify-between mb-8">
            <div className="text-3xl font-bold text-[#23A892]">
              {stats.averageRating.toFixed(1)}
            </div>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <svg 
                  key={i} 
                  className={`w-5 h-5 ${i < Math.round(stats.averageRating) ? 'text-amber-400 drop-shadow-sm' : 'text-gray-300'}`} 
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          </div>

          <div className="mb-6 text-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#23A892]/10 text-gray-700 border border-[#23A892]/20">
              {t('reviews.basedOn', 'Basé sur {{count}} avis', { count: stats.count })}
            </span>
          </div>

          <div className="space-y-4">
            {Object.entries(stats.ratingDistribution)
              .sort((a, b) => Number(b[0]) - Number(a[0]))
              .map(([rating, count]) => {
                const percentage = (count / stats.count) * 100;
                return (
                  <div key={rating} className="flex items-center group/bar">
                    <div className="w-12 text-sm font-semibold text-gray-800 flex items-center gap-1">
                      {rating}
                      <svg className="w-3 h-3 text-[#23A892]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </div>
                    <div className="flex-grow mx-4">
                      <div className="h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                        <div 
                          className="h-full bg-[#23A892] rounded-full transition-all duration-1000 ease-out shadow-sm group-hover/bar:shadow-md" 
                          style={{ 
                            width: `${percentage}%`,
                            animationDelay: `${Number(rating) * 100}ms`
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="w-14 text-sm font-semibold text-gray-700 text-right">
                      {count}
                    </div>
                  </div>
                );
              })}
          </div>

          {professionalId && (
            <>
              <div className="mt-8">
                <Button
                  onClick={() => setIsModalOpen(true)}
                  variant="primary"
                  className="w-full bg-[#23A892] hover:bg-[#1e9076] text-white font-semibold py-4 rounded-2xl shadow-lg"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-5 w-5" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.828 8.332-1.151z"/>
                  </svg>
                  <span className="ml-3">
                    {t('reviews.viewAll')}
                  </span>
                </Button>
              </div>
              
              {/* Portail pour le modal d'affichage des avis */}
              <ReviewModalPortal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                professionalId={professionalId} 
              />
            </>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-full py-12 text-center">
          <div className="relative mb-6">
            <div className="relative bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-[#23A892]/20">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-16 w-16 text-gray-400 mx-auto" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" 
                />
              </svg>
            </div>
          </div>
          <p className="text-lg text-gray-600 font-medium">
            {t('reviews.noReviews')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProfessionalStats;
